// GDPR cookie consent banner - bottom left, Google Consent Mode v2 integration
// Branch: develop

import React, { useState, useEffect } from 'react';
import { initFirebaseAnalytics, syncAnalyticsConsent } from '../utils/analytics';

// Cookie preferences structure
interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  functionality: boolean;
  personalization: boolean;
}

const CookieConsentBanner: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false,
    functionality: false,
    personalization: false,
  });

  useEffect(() => {
    if (!showBanner) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showBanner]);

  // Load saved preferences or show banner
  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');

    if (!consent) {
      setShowBanner(true);
    } else {
      try {
        const savedPrefs = JSON.parse(consent) as CookiePreferences;
        setPreferences(savedPrefs);

        // Update Google Consent Mode
        if (typeof window !== 'undefined' && (window as any).gtag) {
          (window as any).gtag('consent', 'update', {
            ad_storage: savedPrefs.marketing ? 'granted' : 'denied',
            ad_user_data: savedPrefs.marketing ? 'granted' : 'denied',
            ad_personalization: savedPrefs.marketing ? 'granted' : 'denied',
            analytics_storage: savedPrefs.analytics ? 'granted' : 'denied',
            personalization_storage: savedPrefs.marketing ? 'granted' : 'denied',
            functionality_storage: 'granted',
            security_storage: 'granted',
          });
        }
        void initFirebaseAnalytics();
        syncAnalyticsConsent(savedPrefs.analytics);
      } catch (error) {
        console.error('Error loading saved consent preferences:', error);
      }
    }
  }, []);

  // Save preferences and update Google Consent Mode
  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem('cookieConsent', JSON.stringify(prefs));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
    setShowCustomize(false);

    // Update Google Consent Mode v2
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        ad_storage: prefs.marketing ? 'granted' : 'denied',
        ad_user_data: prefs.marketing ? 'granted' : 'denied',
        ad_personalization: prefs.marketing ? 'granted' : 'denied',
        analytics_storage: prefs.analytics ? 'granted' : 'denied',
        personalization_storage: prefs.marketing ? 'granted' : 'denied',
        functionality_storage: 'granted',
        security_storage: 'granted',
      });

      // console.log('✅ Google Consent Mode updated:', {
      //   analytics: prefs.analytics ? 'granted' : 'denied',
      //   marketing: prefs.marketing ? 'granted' : 'denied',
      // });
    } else {
      // console.log('⚠️ Google Consent Mode not available');
    }

    void initFirebaseAnalytics();
    syncAnalyticsConsent(prefs.analytics);
  };

  // Accept all cookies
  const handleAcceptAll = () => {
    savePreferences({
      essential: true,
      analytics: true,
      marketing: true,
      functionality: true,
      personalization: true,
    });
  };

  // Reject all optional cookies
  const handleRejectAll = () => {
    savePreferences({
      essential: true,
      analytics: false,
      marketing: false,
      functionality: false,
      personalization: false,
    });
  };

  // Save custom preferences
  const handleSaveCustom = () => {
    savePreferences(preferences);
  };

  if (!showBanner) return null;

  return (
    <>
      <div className="fixed inset-0 z-[100001] bg-black/70" aria-hidden="true" />
      <div
        className="fixed bottom-6 left-6 z-[100002] max-w-md"
        role="dialog"
        aria-modal="true"
        aria-label="Cookie consent"
      >
        <div className="bg-black rounded-2xl p-8 w-full shadow-2xl">
          {!showCustomize ? (
            <>
              {/* Simple consent view */}
              <div className="flex items-center gap-3 mb-4">
                <span className="text-4xl">🍪</span>
                <h3 className="text-2xl font-bold text-white">We use cookies</h3>
              </div>

              <p className="text-gray-300 text-base mb-6 leading-relaxed">
                We use cookies to enhance your experience, analyze site traffic, and personalize
                content. Accept or decline to continue.
              </p>

              <div className="space-y-3">
                <button
                  onClick={handleAcceptAll}
                  className="w-full px-6 py-3 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-lg transition-colors"
                >
                  Sure, that&apos;s fine
                </button>
                <button
                  onClick={handleRejectAll}
                  className="w-full px-6 py-3 bg-transparent hover:bg-gray-800 text-white font-medium rounded-lg transition-colors border border-gray-700"
                >
                  Decline
                </button>
              </div>

              <div className="text-center mt-6">
                <button
                  onClick={() => setShowCustomize(true)}
                  className="text-gray-400 hover:text-white underline text-sm transition-colors"
                >
                  Manage preferences
                </button>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              {/* Detailed preferences view */}
              <div className="flex items-center gap-3 mb-6">
                <span className="text-2xl">⚙️</span>
                <h3 className="text-xl font-bold text-white">Cookie Preferences</h3>
              </div>

              <p className="text-sm text-gray-300 mb-4">
                Choose which types of cookies you want to allow:
              </p>

              <div className="space-y-3">
                {/* Necessary - always enabled */}
                <div className="flex items-start justify-between p-4 bg-gray-800 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-white mb-1">Necessary cookies</h4>
                    <p className="text-sm text-gray-400">Essential for the website to function</p>
                  </div>
                  <div className="ml-4 flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={preferences.essential}
                      disabled
                      className="w-5 h-5 text-emerald-500 bg-gray-700 border-gray-600 rounded focus:ring-emerald-500"
                    />
                    <span className="text-sm text-gray-400">Always active</span>
                  </div>
                </div>

                {/* Analytics */}
                <div className="flex items-start justify-between p-4 bg-gray-800 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-white mb-1">Analytics cookies</h4>
                    <p className="text-sm text-gray-400">
                      Help us understand how visitors use our site
                    </p>
                  </div>
                  <div className="ml-4">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={e =>
                        setPreferences({
                          ...preferences,
                          analytics: e.target.checked,
                        })
                      }
                      className="w-5 h-5 text-emerald-500 bg-gray-700 border-gray-600 rounded focus:ring-emerald-500 cursor-pointer"
                    />
                  </div>
                </div>

                {/* Marketing */}
                <div className="flex items-start justify-between p-4 bg-gray-800 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-white mb-1">Marketing cookies</h4>
                    <p className="text-sm text-gray-400">
                      Used to deliver personalized advertisements
                    </p>
                  </div>
                  <div className="ml-4">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={e =>
                        setPreferences({
                          ...preferences,
                          marketing: e.target.checked,
                        })
                      }
                      className="w-5 h-5 text-emerald-500 bg-gray-700 border-gray-600 rounded focus:ring-emerald-500 cursor-pointer"
                    />
                  </div>
                </div>

                {/* Functionality */}
                <div className="flex items-start justify-between p-4 bg-gray-800 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-white mb-1">Functionality cookies</h4>
                    <p className="text-sm text-gray-400">Remember your preferences and settings</p>
                  </div>
                  <div className="ml-4">
                    <input
                      type="checkbox"
                      checked={preferences.functionality}
                      onChange={e =>
                        setPreferences({
                          ...preferences,
                          functionality: e.target.checked,
                        })
                      }
                      className="w-5 h-5 text-emerald-500 bg-gray-700 border-gray-600 rounded focus:ring-emerald-500 cursor-pointer"
                    />
                  </div>
                </div>

                {/* Personalization */}
                <div className="flex items-start justify-between p-4 bg-gray-800 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-white mb-1">Personalization cookies</h4>
                    <p className="text-sm text-gray-400">
                      Provide personalized content and features
                    </p>
                  </div>
                  <div className="ml-4">
                    <input
                      type="checkbox"
                      checked={preferences.personalization}
                      onChange={e =>
                        setPreferences({
                          ...preferences,
                          personalization: e.target.checked,
                        })
                      }
                      className="w-5 h-5 text-emerald-500 bg-gray-700 border-gray-600 rounded focus:ring-emerald-500 cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-4">
                <button
                  onClick={handleSaveCustom}
                  className="w-full px-4 py-3 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-lg transition-colors"
                >
                  Save Preferences
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="w-full px-4 py-3 bg-transparent hover:bg-gray-800 text-white font-medium rounded-lg transition-colors border border-gray-600"
                >
                  Accept All
                </button>
                <button
                  onClick={() => setShowCustomize(false)}
                  className="w-full px-4 py-3 bg-transparent hover:bg-gray-800 text-white font-medium rounded-lg transition-colors border border-gray-600"
                >
                  Back
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieConsentBanner;
